import React from "react";
import { Link } from "react-router-dom";
import { formatDateToView } from "../../functions/DateTime";
import { getFacilitator, getParticipants, getRequester } from "../../functions/FilterUsers";
import BulletImageList from "../ParticipantsSection/RenderBulletImage";
import './FeedbackGroupByRole.scss';
import { t } from "i18next";

interface FeedbackGroupByRoleProps {
  role: 'requester' | 'facilitator' | 'participant';
  feedbacks: IFeedback[];
  userId: string;
}

export function FeedbackGroupByRole({ role, feedbacks, userId }: FeedbackGroupByRoleProps) {
    const filteredFeedbacks = feedbacks.filter(feedback =>
      feedback.users.some(user =>
        user.cognito_id === userId &&
        user.role === role &&
        (user.role === 'requester' || user.confirmed_at !== null && user.confirmed_at !== undefined)
      )
  );

  if (filteredFeedbacks.length === 0) return null;
  const titleMap = {
    requester: t('feedbackList.titles.requester') + ":",
    facilitator: t('feedbackList.titles.facilitator') + ":",
    participant: t('feedbackList.titles.participant') + ":",
  };

  return (
    <>
      <h5 className="mb-3"><strong>{titleMap[role]}</strong></h5>

      {/* Desktop Layout - List Group */}
      <div className="card w-100 mb-4 d-none d-md-block">
        <div className="list-group list-group-flush">
          {filteredFeedbacks.map(feedback => (
            <div
              className="list-group-item d-flex flex-column flex-md-row p-3 justify-content-between align-items-start align-items-md-center gap-3 flex-row"
              key={feedback.id}
            >
              <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3 ">
                <div className="d-flex gap-3">
                  <div className="d-flex align-items-center gap-3 group">
                    <BulletImageList
                      title={t('feedbackRoles.requester') + ':'}
                      userList={[getRequester(feedback.users) as IUser]} />
                  </div>
                  <div className="d-flex align-items-center gap-3 group">
                    <span className="separator">|</span>
                    <BulletImageList
                      title={t("feedbackRoles.facilitator") + ':'}
                      userList={[getFacilitator(feedback.users) as IUser]} />
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3 group">
                  <span className="separator">|</span>
                  <BulletImageList
                    title={t('feedbackRoles.participants') + ':'}
                    userList={getParticipants(feedback.users)} />
                </div>
              </div>

              <div className="d-flex w-100 justify-content-between justify-content-md-end align-items-center gap-3">
                <div className="d-flex flex-column flex-md-row gap-3">
                  <p className="m-0 text-secondary">
                    {`${formatDateToView(feedback.schedule_date)} ${feedback.status !== 'completed' ? `${t("feedbackDate.preposition")} ${feedback.schedule_time}` : ''}`}
                  </p>
                  <span className={`badge rounded-1 py-2 px-4 fixed-badge status-badge ${feedback.status !== 'completed' ? 'pending-feedback' : 'completed-feedback'}`}>
                    {feedback.status !== 'completed' ? t("feedbackList.status.pending") : t("feedbackList.status.completed")}
                  </span>
                </div>
                <div className="d-flex flex-column flex-md-row gap-3">
                  <Link
                    to={`/feedbacks/${feedback.id}/show`}
                    className="btn btn-outline-secondary fixed-width-btn"
                  >
                    {t("feedbackList.actions.showDetails")}
                  </Link>
                  <Link
                    to={`/feedbacks/${feedback.id}/canvas`}
                    className="btn btn-outline-secondary fixed-width-btn"
                  >
                    {feedback.status !== 'completed' ? t("feedbackList.actions.openBoard") : t("feedbackList.actions.showBoard")}
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile Layout - Individual Cards */}
      <div className="d-md-none">
        {filteredFeedbacks.map(feedback => (
          <div className="card mb-3 feedback-card-mobile" key={feedback.id}>
            <div className="card-body">
              <div className="d-flex flex-column flex-md-row p-3 justify-content-between align-items-start align-items-md-center gap-3 flex-row">
                <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3 ">
                  <div className="d-flex gap-3">
                    <div className="d-flex align-items-center gap-3 group">
                      <BulletImageList
                        title={t('feedback.roles.requester') + ':'}
                        userList={[getRequester(feedback.users) as IUser]} />
                    </div>
                    <div className="d-flex align-items-center gap-3 group">
                      <BulletImageList
                        title={t("feedback.rolesfacilitator") + ':'}
                        userList={[getFacilitator(feedback.users) as IUser]} />
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-3 group">
                    <BulletImageList
                      title={t('feedback.roles.participant') + ':'}
                      userList={getParticipants(feedback.users)} />
                  </div>
                </div>

                <div className="d-flex w-100 justify-content-between justify-content-md-end align-items-center gap-3">
                  <div className="d-flex flex-column flex-md-row gap-3">
                    <p className="m-0 text-secondary">
                      {`${formatDateToView(feedback.schedule_date)} ${feedback.status !== 'completed' ? `${t("feedbackDate.preposition")} ${feedback.schedule_time}` : ''}`}
                    </p>
                    <span className={`badge rounded-1 py-2 px-4 fixed-badge ${feedback.status !== 'completed' ? 'pending-feedback' : 'completed-feedback'}`}>
                      {feedback.status !== 'completed' ? t("feedbackList.status.pending") : t("feedbackList.status.completed")}
                    </span>
                  </div>
                  <div className="d-flex flex-column flex-md-row gap-3">
                    <Link
                      to={`/feedbacks/${feedback.id}/show`}
                      className="btn btn-outline-secondary fixed-width-btn"
                    >
                      {t("feedbackList.actions.showDetails")}
                    </Link>
                    <Link
                      to={`/feedbacks/${feedback.id}/canvas`}
                      className="btn btn-outline-secondary fixed-width-btn"
                    >
                      {feedback.status !== 'completed' ? t("feedbackList.actions.openBoard") : t("feedbackList.actions.showBoard")}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
